import { useCallback, useRef } from "react";
import "src/types/window";
import {
    ELIGIBILITY_RESULT_PAGE_NAME,
    ELIGIBILITY_RESULT_TOOL,
    ELIGIBILITY_RESULT_TRACK_EVENT,
    FORM_ERRORS_EVENT_ACTION,
    FORM_ERRORS_PAGE_NAME,
    FORM_ERRORS_TRACK_EVENT,
    FORM_ERRORS_TRACKING_PREFIX,
    ON_CLICK_CONTENT_TYPE,
    PAGE_LOAD_TOOL,
    PAGE_LOAD_TRACK_EVENT,
} from "src/consts/analytics";
import { CreditDecision } from "src/pages/form-page/make-credit-decision/make-credit-decision.types";
import { FormErrorData, UseAdobeAnalyticsReturn } from "src/types/analytics";

const isAdobeAnalyticsAvailable = (): boolean => {
    return !!window?.digitaldata && !!window?._satellite;
};

export const useAdobeAnalytics = (cookiePreferences: boolean): UseAdobeAnalyticsReturn => {
    const lastTrackedErrorsRef = useRef<string | null>(null);

    const trackInitialPageLoad = useCallback(() => {
        if (!cookiePreferences || !isAdobeAnalyticsAvailable()) {
            return;
        }

        window.digitaldata = {
            ...window.digitaldata,
            page: {
                ...window.digitaldata?.page,
                tool: PAGE_LOAD_TOOL,
            },
            onclick: {
                ...window.digitaldata?.onclick,
                contentType: ON_CLICK_CONTENT_TYPE,
            },
        };

        window._satellite?.track?.(PAGE_LOAD_TRACK_EVENT);
    }, [cookiePreferences]);

    const trackEligibilityResult = useCallback(
        (result: CreditDecision) => {
            if (!cookiePreferences || !isAdobeAnalyticsAvailable()) {
                return;
            }

            const mappedResult = result.decision === "APPROVED" ? "approved" : "referred";

            window.digitaldata = {
                ...window.digitaldata,
                page: {
                    ...window.digitaldata?.page,
                    pageName: ELIGIBILITY_RESULT_PAGE_NAME,
                    pageNameNoVehicle: ELIGIBILITY_RESULT_PAGE_NAME,
                    variantName: ELIGIBILITY_RESULT_TRACK_EVENT,
                    tool: ELIGIBILITY_RESULT_TOOL,
                    errorTracking: "",
                },
                onclick: {
                    ...window.digitaldata?.onclick,
                    contentType: ON_CLICK_CONTENT_TYPE,
                },
                credit: {
                    ...window.digitaldata?.credit,
                    financialApplicationStatus: mappedResult,
                },
                event: {
                    ...window.digitaldata?.event,
                    action: "",
                },
            };

            window._satellite?.track?.(ELIGIBILITY_RESULT_TRACK_EVENT);
        },
        [cookiePreferences]
    );

    const trackFormErrorsOnSubmission = useCallback(
        (fieldErrors: FormErrorData) => {
            if (!cookiePreferences || !isAdobeAnalyticsAvailable()) {
                return;
            }

            const mappedFormErrors = fieldErrors
                .map(({ fieldName, errorType }) => {
                    const humanReadableFieldName = fieldName
                        .replaceAll(/([A-Z])/g, " $1")
                        .toLowerCase()
                        .trim();
                    return `${FORM_ERRORS_TRACKING_PREFIX}:${humanReadableFieldName}:${errorType}`;
                })
                .join(", ");

            // Reduce repeat logs
            if (mappedFormErrors === lastTrackedErrorsRef.current) {
                return;
            }
            lastTrackedErrorsRef.current = mappedFormErrors;

            window.digitaldata = {
                ...window.digitaldata,
                page: {
                    ...window.digitaldata?.page,
                    pageName: FORM_ERRORS_PAGE_NAME,
                    pageNameNoVehicle: FORM_ERRORS_PAGE_NAME,
                    errorTracking: mappedFormErrors,
                },
                onclick: {
                    ...window.digitaldata?.onclick,
                    contentType: ON_CLICK_CONTENT_TYPE,
                },
                event: {
                    ...window.digitaldata?.event,
                    action: FORM_ERRORS_EVENT_ACTION,
                },
            };

            window._satellite?.track?.(FORM_ERRORS_TRACK_EVENT);
        },
        [cookiePreferences]
    );

    return {
        trackInitialPageLoad,
        trackEligibilityResult,
        trackFormErrorsOnSubmission,
    };
};
